/* eslint-disable camelcase */

import AIProcessingWidget from '@components/AIprocessing/AIProcessingWidget';
import AuditAINotification from '@components/AuditAINotification';
import FrontendV2Banner from '@components/FrontendV2Banner';
import ProfileDropdown from '@components/ProfileDropdown';
import Tooltip from '@components/tooltip';
import V2LaunchDialog from '@components/V2LaunchDialog';
import {
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@retracedgmbh/ui-components-lib';
import Typography from '@retracedgmbh/ui-components-lib/Typography';
import { useAuth } from '@state/auth';
import { useToggleIntercomWidget } from '@state/intercom';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useEnabledDevelopmentTags from '@/hooks/useEnabledDevelopmentTags';
import useTranslator from '@/hooks/useTranslator';
import { isCompanyless } from '@/lib/companyless';
import { useNavbar } from '@/state/navbar';
import Drawer from './drawer-partials';
import Sidebar from './sidebar';

const Navbar = () => {
  const { isGreenPathEnabled } = useEnabledDevelopmentTags();
  const { getTranslation, i18n } = useTranslator({ page: 'navbar' });
  const { auditAI } = useNavbar(state => ({
    auditAI: state.auditAI,
  }));
  const [_isAiUsageDrawerOpen, _setIsAiUsageDrawerOpen] = React.useState(false);
  const { pathname } = useLocation();
  const isAdminPage = pathname.startsWith('/management');

  const matchPathToName = React.useMemo(
    () => [
      { regex: /\/$/, pageTitle: getTranslation('navbar.pageTitle.dashboard') },
      { regex: /(\/management\/companies)/, pageTitle: 'Companies' },
      {
        regex: /(\/management\/components)/,
        pageTitle: 'Frontend Components Library',
      },
      { regex: /\/management\/users/, pageTitle: 'Users' },
      { regex: /\/management\/impersonate/, pageTitle: 'Impersonate' },
      {
        regex: /\/products\/list\/archived/,
        pageTitle: getTranslation('navbar.pageTitle.productsArchive'),
      },
      {
        regex: /\/products\//,
        pageTitle: getTranslation('navbar.pageTitle.products'),
      },
      {
        regex: /\/product-components\/list-archived/,
        pageTitle: getTranslation('navbar.pageTitle.componentsArchive'),
      },
      {
        regex: /\/product-components\//,
        pageTitle: getTranslation('navbar.pageTitle.components'),
      },
      {
        regex: /\/my-styles\/variants\/batches/,
        pageTitle: getTranslation('navbar.pageTitle.variants.batches'),
      },
      {
        regex: /\/my-styles\/skus\/batches/,
        pageTitle: getTranslation('navbar.pageTitle.skus.batches'),
      },
      {
        regex: /\/my-styles\/styles\/batches/,
        pageTitle: getTranslation('navbar.pageTitle.styles.batches'),
      },
      {
        regex: /\/my-styles\/variants/,
        pageTitle: getTranslation('navbar.pageTitle.variant'),
      },
      {
        regex: /\/my-styles\/skus/,
        pageTitle: getTranslation('navbar.pageTitle.sku'),
      },
      {
        regex: /\/my-styles/,
        pageTitle: getTranslation('navbar.pageTitle.style'),
      },
      {
        regex: /\/my-components/,
        pageTitle: getTranslation('navbar.pageTitle.myComponents'),
      },
      { regex: /\/management\/badges/, pageTitle: 'Consumer Product Badges' },
      {
        regex: /\/companies\/list/,
        pageTitle: getTranslation('navbar.pageTitle.networkOverview'),
      },
      {
        regex: /\/companies/,
        pageTitle: getTranslation('navbar.pageTitle.company'),
      },
      {
        regex: /\/companies\/.*\/environmental-data/,
        pageTitle: getTranslation('navbar.pageTitle.environmentalData'),
      },
      {
        regex: /\/claims/,
        pageTitle: getTranslation('navbar.pageTitle.certifications'),
      },
      {
        regex: /\/audits/,
        pageTitle: getTranslation('navbar.pageTitle.audits'),
      },
      {
        regex: /\/trace/,
        pageTitle: getTranslation('navbar.pageTitle.tracing'),
      },
      {
        regex: /\/company-documents/,
        pageTitle: getTranslation('navbar.pageTitle.documents'),
      },
      {
        regex: /\/cascade-tracing\/archived/,
        pageTitle: getTranslation('navbar.pageTitle.ordersArchive'),
      },
      {
        regex: /\/orders/,
        pageTitle: getTranslation('navbar.pageTitle.orders'),
      },
      {
        regex: /\/order-lines/,
        pageTitle: getTranslation('navbar.pageTitle.orderLines'),
      },
      {
        regex: /\/supply-chains\/v2\/archived/,
        pageTitle: getTranslation('navbar.pageTitle.supplyChainsArchive'),
      },
      {
        regex: /\/company-chains\/v2\/archived/,
        pageTitle: getTranslation('navbar.pageTitle.supplyChainsArchive'),
      },
      {
        regex: /\/supply-chains/,
        pageTitle: getTranslation('navbar.pageTitle.supplyChains'),
      },
      {
        regex: /^\/standards/,
        pageTitle: getTranslation('navbar.pageTitle.directory'),
      },
      { regex: /^\/management\/standards/, pageTitle: 'Manage Directory' },
      {
        regex: /^\/management\/question-template/,
        pageTitle: 'Manage Question Templates',
      },
      { regex: /^\/management\/document-types/, pageTitle: 'Document Types' },
      { regex: /^\/management\/audit-types/, pageTitle: 'Audit Types' },
      { regex: /^\/management\/audits/, pageTitle: 'Audits' },
      { regex: /^\/management\/documents/, pageTitle: 'Documents' },
      { regex: /^\/management\/files/, pageTitle: 'Files' },
      { regex: /^\/management\/slcp-dashboard/, pageTitle: 'SLCP Dashboard' },
      {
        regex: /^\/management\/risk-analysis/,
        pageTitle: 'Manage Risk Analysis',
      },
      { regex: /^\/management\/risk-management/, pageTitle: 'Risk Management' },
      { regex: /^\/management\/webshops/, pageTitle: 'Manage Webshops' },
      {
        regex: /^\/management\/origins\/list/,
        pageTitle: 'Manage Webshop Origins',
      },
      {
        regex: /^\/management\/platform-features/,
        pageTitle: 'Manage Feature Access',
      },
      {
        regex: /^\/management\/development-tags/,
        pageTitle: 'Manage Development Tags',
      },
      { regex: /^\/management\/materials/, pageTitle: 'Manage Materials' },
      {
        regex: /^\/management\/product-categories\/.*\/fields/,
        pageTitle: 'Manage Product Categories Fields',
      },
      {
        regex: /^\/management\/product-categories/,
        pageTitle: 'Manage Product Categories',
      },
      {
        regex: /^\/management\/facility-processes/,
        pageTitle: 'Manage Facility Processes',
      },
      { regex: /^\/management\/countries/, pageTitle: 'Manage Countries' },
      {
        regex: /^\/management\/production-processes/,
        pageTitle: 'Manage Production Processes',
      },
      {
        regex: /^\/management\/attachment-templates/,
        pageTitle: 'Attachment templates',
      },
      {
        regex: /^\/management\/custom-information-fields/,
        pageTitle: 'Manage common custom information fields',
      },
      {
        regex: /^\/management\/supply-chains/,
        pageTitle: 'Manage Supply Chains',
      },
      {
        regex: /^\/management\/fiber-coc-models/,
        pageTitle: 'Manage Coc Models',
      },
      {
        regex: /^\/management\/fiber-settings/,
        pageTitle: 'Manage Fiber Settings',
      },
      {
        regex: /^\/management\/tag-identifications/,
        pageTitle: 'Manage Tag Identifications',
      },
      {
        regex: /\/profile/,
        pageTitle: getTranslation('navbar.pageTitle.profile'),
      },
      {
        regex: /\/transaction-certificate-management/,
        pageTitle: getTranslation(
          'navbar.pageTitle.transactionCertificateManagement',
        ),
      },
      {
        regex: /\/certified-materials-management/,
        pageTitle: getTranslation(
          'navbar.pageTitle.certifiedMaterialsManagement',
        ),
      },
      {
        regex: /^\/reports\/.*\/drilldown/,
        pageTitle: getTranslation('navbar.pageTitle.reports.dataDrilldown'),
      },
      {
        regex: /^\/reports\/.*\/tracing-operations/,
        pageTitle: getTranslation('navbar.pageTitle.tracingOperations'),
      },
      {
        regex: /^\/reports\/.*\/tracing-compliance/,
        pageTitle: getTranslation('navbar.pageTitle.tracingCompliance'),
      },
      {
        regex: /^\/reports\/.*\/audit-compliance/,
        pageTitle: getTranslation('navbar.pageTitle.auditCompliance'),
      },
      {
        regex: /^\/reports\/.*\/certificate-compliance/,
        pageTitle: getTranslation('navbar.pageTitle.certificateCompliance'),
      },

      {
        regex: /^\/management\/fiber-production-process/,
        pageTitle: getTranslation(
          'navbar.pageTitle.manageProductionProcessTemplates',
        ),
      },
      {
        regex: /^\/management\/fiber-production-factors/,
        pageTitle: getTranslation('navbar.pageTitle.manageProductionFactors'),
      },
      { regex: /\/cascade-tracing\/file-center/, pageTitle: 'File Center' },
      {
        regex: /\/cascade-tracing/,
        pageTitle: getTranslation('navbar.pageTitle.orders'),
      },
      {
        regex: /^\/activities-feed\/list/,
        pageTitle: getTranslation('navbar.pageTitle.activityFeed'),
      },
      {
        regex: /^\/attachment-templates\/list/,
        pageTitle: getTranslation('navbar.pageTitle.tracingPreferences'),
      },
      {
        regex: /^\/attachment-templates\/.*\/view/,
        pageTitle: getTranslation('navbar.pageTitle.tracingPreference'),
      },

      {
        regex: /^\/fiber\/transactions\/outgoing\/.*\/view/,
        pageTitle: getTranslation('navbar.pageTitle.outgoingTransactions'),
      },
      {
        regex: /^\/fiber\/transactions\/incoming\/.*\/view/,
        pageTitle: getTranslation('navbar.pageTitle.incomingTransactions'),
      },

      {
        regex: /^\/fiber\/inventory\/archived-lots/,
        pageTitle: getTranslation('navbar.pageTitle.archivedLots'),
      },
      {
        regex: /^\/fiber\/inventory/,
        pageTitle: getTranslation('navbar.pageTitle.inventory'),
      },
      {
        regex: /^\/fiber\/transactions/,
        pageTitle: getTranslation('navbar.pageTitle.transactions'),
      },
      {
        regex: /^\/fiber\/transformations/,
        pageTitle: getTranslation('navbar.pageTitle.productions'),
      },
      {
        regex: /\/transactions/,
        pageTitle: getTranslation('navbar.pageTitle.blockchainTransactions'),
      },
      {
        regex: /\/constants-mapping/,
        pageTitle: getTranslation('navbar.pageTitle.constantsMapping'),
      },
      {
        regex: /\/api-keys/,
        pageTitle: getTranslation('navbar.pageTitle.apiKey'),
      },

      {
        regex: /^\/risk-rules/,
        pageTitle: getTranslation('navbar.pageTitle.riskAssessmentPrefrences'),
      },
      {
        regex: /^\/management\/relationship-types/,
        pageTitle: getTranslation('navbar.pageTitle.relationshipTypes'),
      },
      {
        regex: /^\/management\/collaboration-statuses/,
        pageTitle: getTranslation('navbar.pageTitle.collaborationStatuses'),
      },
      {
        regex: /^\/questionnaire/,
        pageTitle: getTranslation('navbar.pageTitle.supplierQuestionnaires'),
      },
      {
        regex: /^\/questionnaires\/archived/,
        pageTitle: getTranslation(
          'navbar.pageTitle.supplierArchivedQuestionnaires',
        ),
      },
      {
        regex: /\/company-chains/,
        pageTitle: getTranslation('navbar.pageTitle.supplyChainMapping'),
      },
      {
        regex: /\/product-chains/,
        pageTitle: getTranslation('navbar.pageTitle.productChainMapping'),
      },
      {
        regex: /\/entity-screening/,
        pageTitle: getTranslation('navbar.pageTitle.entityScreening'),
      },
      {
        regex: /\/entity-screening\/[^/]+\/screening-results$/,
        pageTitle: getTranslation('navbar.pageTitle.entityScreeningResults'),
      },
      {
        regex: /\/involved-orders/,
        pageTitle: getTranslation(
          'navbar.pageTitle.entityScreeningInvolvedOrders',
        ),
      },
      {
        regex: /^\/capa-v2\/actions\/.*\/details/,
        pageTitle: getTranslation('navbar.pageTitle.action'),
      },
      {
        regex: /^\/capa-v2\/plans\/.*\/details/,
        pageTitle: getTranslation('navbar.pageTitle.plan'),
      },
    ],
    [i18n.language],
  );
  const location = useLocation();
  const navigate = useNavigate();
  const toggleWidget = useToggleIntercomWidget(state => state.toggleWidget);

  const { me, companyId, isImpersonating } = useAuth(state => ({
    me: state.me,
    companyId: state.companyId,
    isImpersonating: state.isImpersonating,
  }));

  const isCompanylessUser = isCompanyless(me, { companyId });

  const [openDrawer, setOpenDrawer] = useState(false);
  const [isProfileDropdownVisible, setIsProfileDropdownVisible] =
    useState(false);

  useEffect(() => {
    const pageTitle = matchPathToName.find(e =>
      location.pathname.match(e.regex),
    )?.pageTitle;
    document.title = pageTitle
      ? `${pageTitle} | retraced Dashboard`
      : 'retraced Dashboard';
    setOpenDrawer(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!openDrawer) {
      setOpenDrawer(false);
    }
  }, [location.pathname, me]);

  if (!me) return null;

  return (
    <>
      <Flex
        direction='column'
        className='tw:fixed tw:top-0 tw:left-0 tw:right-0 tw:z-30'
      >
        <FrontendV2Banner />
        <V2LaunchDialog />
        <nav
          className='navbar'
          aria-label='dropdown navigation'
          style={{ borderBottomWidth: 1, boxShadow: 'none' }}
        >
          <div className='navbar-brand'>
            <div className='navbar-item tw:px-3 tw:py-0'>
              <Link to={'/'} className='is-hidden-mobile'>
                <img
                  alt='retraced logo'
                  src={`${
                    import.meta.env.VITE_CDN_URI
                  }/static/dashboard-service/retraced-brand-icon-primary-v2.svg`}
                  width='25'
                  height='35'
                  loading='lazy'
                />
              </Link>
              <div className='is-hidden-tablet'>
                <Tooltip
                  key='get-support-tooltip'
                  placement='bottom'
                  trigger={props => (
                    <button
                      {...props}
                      type='button'
                      className='button is-white'
                      onClick={() => setOpenDrawer(true)}
                    >
                      <span className='icon'>
                        <i className='fa-light fa-bars' />
                      </span>
                    </button>
                  )}
                >
                  <p className='help'>
                    {getTranslation('navbar.menu.tooltip')}
                  </p>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className='navbar-menu'>
            <div
              className='navbar-start is-hidden-tablet'
              style={{ width: '100%' }}
            >
              <div
                className='navbar-item tw:px-1 tw:py-0'
                style={{ margin: 'auto' }}
              >
                <Link to={'/'}>
                  <img
                    alt='retraced logo'
                    src={`${import.meta.env.VITE_CDN_URI}/static/dashboard-service/secondary-logo-black-v2.svg?auto=format`}
                    width='120'
                    height='50'
                    loading='lazy'
                  />
                </Link>
              </div>
            </div>
            <div className='navbar-start is-hidden-mobile'>
              <div className='navbar-item tw:ml-5 tw:px-1 tw:py-0'>
                <Tooltip
                  key='get-support-tooltip'
                  placement='bottom'
                  trigger={props => (
                    <button
                      {...props}
                      type='button'
                      className='button is-white'
                      onClick={() => navigate(-1)}
                    >
                      <span className='icon'>
                        <i className='fa-light fa-chevron-left' />
                      </span>
                    </button>
                  )}
                >
                  <p className='help'>
                    {getTranslation('navbar.previousPage.tooltip')}
                  </p>
                </Tooltip>
              </div>
              <div className='navbar-item tw:px-1 tw:py-0'>
                <Tooltip
                  key='get-support-tooltip'
                  placement='bottom'
                  trigger={props => (
                    <button
                      {...props}
                      type='button'
                      className='button is-white'
                      onClick={() => navigate(1)}
                    >
                      <span className='icon'>
                        <i className='fa-light fa-chevron-right' />
                      </span>
                    </button>
                  )}
                >
                  <p className='help'>
                    {getTranslation('navbar.nextPage.tooltip')}
                  </p>
                </Tooltip>
              </div>
              <div className='navbar-item'>
                <Typography component={'h6'} variant={'h6'} fontWeight='bold'>
                  {
                    matchPathToName.find(e => location.pathname.match(e.regex))
                      ?.pageTitle
                  }
                </Typography>
              </div>
            </div>

            <div className='navbar-end mr-2'>
              {isGreenPathEnabled && (auditAI.processing || auditAI.done) && (
                <AuditAINotification badgeCount={auditAI.done && '1'} />
              )}
              {!isAdminPage && !isCompanylessUser && (
                <div className='navbar-item tw:px-2 tw:py-0'>
                  <AIProcessingWidget />
                </div>
              )}
              <div className='navbar-item tw:px-1 tw:py-0'>
                <Tooltip
                  key='get-support-tooltip'
                  placement='bottom'
                  trigger={() => (
                    <Link
                      to='/activities-feed/list'
                      className='button is-white'
                    >
                      <span className='icon'>
                        <i className='fa-light fa-bell' />
                      </span>
                    </Link>
                  )}
                >
                  <p className='help'>
                    {getTranslation('navbar.notifications.tooltip')}
                  </p>
                </Tooltip>
              </div>
              <div className='navbar-item tw:px-1 tw:py-0'>
                <Tooltip
                  key='get-support-tooltip'
                  placement='bottom'
                  trigger={props => (
                    <button
                      type='button'
                      onClick={() => {
                        if (window?.Intercom) {
                          window.Intercom('update', {
                            hide_default_launcher: false,
                          });
                          toggleWidget(true);
                        }
                      }}
                      data-cy='btn-intercom-launcher'
                      {...props}
                      className='button is-white intercom-launcher'
                    >
                      <span className='icon'>
                        <i className='fa-light fa-question-circle' />
                      </span>
                    </button>
                  )}
                >
                  <p className='help'>
                    {getTranslation('navbar.getHelp.tooltip')}
                  </p>
                </Tooltip>
              </div>

              <div className='tw:hidden tw:items-center tw:md:flex tw:px-sm tw:cursor-pointer tw:hover:bg-background-secondary'>
                <Popover
                  open={isProfileDropdownVisible}
                  onOpenChange={setIsProfileDropdownVisible}
                >
                  <PopoverTrigger asChild>
                    <Flex alignItems='center'>
                      <figure className='tw:w-10 tw:h-10'>
                        <img
                          alt='not found'
                          className='tw:rounded-full'
                          src={`${
                            me?.company?.featuredImageFileUrl ||
                            `${import.meta.env.VITE_CDN_URI}/static/dashboard-service/image-404.jpg`
                          }?fit=crop&auto=format&h=96&w=96${isImpersonating() ? '&border=48,55FF0000' : ''}`}
                          height='40'
                          width='40'
                        />
                      </figure>
                      <strong
                        className='tw:ml-2 ellipsis'
                        style={{ maxWidth: '130px' }}
                      >
                        {me?.company?.name}
                      </strong>
                      <i className='fa-solid tw:text-sm fa-chevron-down tw:ml-2' />
                    </Flex>
                  </PopoverTrigger>
                  <PopoverContent className='tw:px-0 tw:py-md tw:max-w-[200px] tw:text-sm'>
                    <ProfileDropdown
                      isImpersonating={isImpersonating()}
                      onHideDropdown={() => setIsProfileDropdownVisible(false)}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        </nav>
      </Flex>
      <div className='is-hidden-tablet'>
        <Drawer isVisible={openDrawer} hide={() => setOpenDrawer(false)}>
          <Sidebar
            accountProps={{
              me,
              isImpersonating: isImpersonating(),
            }}
          />
        </Drawer>
      </div>
    </>
  );
};

export default Navbar;
